import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  PageContainer,
  PageHeader,
  PageMainCard,
  PageMainStack,
} from "@volca/ui/components/ui/page-components";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import {
  SETTINGS_PAGE_TITLES,
  SIDEBAR_MODES,
  SIDEBAR_SECTIONS,
} from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { dayjs } from "@/core/utils/date";
import { useGetReferralSources } from "@/referral-sources/query";
import { ReferralSource } from "@/referral-sources/referral-source";
import { ReferralSourcesTable } from "./referral-sources-table";

const ReferralSources = () => {
  const { businessId } = useParams();
  const { data: referralSources = [], isLoading } =
    useGetReferralSources(businessId);
  const {
    updateSidebar,
    state: { selectedItemId },
  } = useBusinessSettingsSidebar();
  const [searchValue, setSearchValue] = useState("");

  const sortedRecords = useMemo(
    () =>
      [...(referralSources as ReferralSource[])].sort(
        (a, b) => dayjs(b.start_date).valueOf() - dayjs(a.start_date).valueOf(),
      ),
    [referralSources],
  );

  const handleRowClick = (referralSource: ReferralSource) => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.referralSources,
      mode: SIDEBAR_MODES.view,
      selectedItemId: referralSource.id,
      isOpen: true,
    });
  };

  const handleNewSource = () => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.referralSources,
      mode: SIDEBAR_MODES.new,
      selectedItemId: undefined,
      isOpen: true,
    });
  };

  return (
    <PageContainer variant="workspace">
      <PageHeader
        title={SETTINGS_PAGE_TITLES["referral-sources"]}
        actions={
          <Button variant="secondary" onClick={handleNewSource}>
            Add source
          </Button>
        }
      />
      <PageMainCard>
        <PageMainStack className="min-h-0 flex-1 pt-6">
          <SearchInput
            placeholder="Search sources"
            value={searchValue}
            onChange={setSearchValue}
          />

          <div className="flex min-h-0 w-full flex-1 flex-col overflow-auto">
            <ReferralSourcesTable
              data={sortedRecords}
              isLoading={isLoading}
              selectedRowId={selectedItemId}
              onRowClick={handleRowClick}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
            />
          </div>
        </PageMainStack>
      </PageMainCard>
    </PageContainer>
  );
};

export default ReferralSources;
